import { RouterLink } from '@/common/components/router-links/RouterLink';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { KeyRound } from 'lucide-react';
import { z } from 'zod';
import { toast } from 'sonner';
import { useNavigate } from '@tanstack/react-router';
import { useAppForm } from '@/lib/form';
import { appFormOptions } from '@/common/form/appFormOptions';
import { requiredInput } from '@/common/form/requiredInput';
import { FormSubmit } from '@/common/components/form-submit/FormSubmit';
import { FormErrors } from '@/common/components/form-errors/FormErrors';
import { AccountService } from '@/features/auth/services/AccountService';

const resetPasswordSchema = z.object({
  newPassword: requiredInput(z.string().min(8)),
  confirmPassword: requiredInput(z.string().min(1)),
});

interface ResetPasswordPageProps {
  token: string;
}

export const ResetPasswordPage = ({ token }: ResetPasswordPageProps) => {
  const navigate = useNavigate();

  const form = useAppForm(
    appFormOptions({
      schema: resetPasswordSchema,
      defaultValues: { newPassword: null, confirmPassword: null },
      onSubmit: async ({ value }) => {
        await AccountService.resetPasswordConfirm({ token, password: value.newPassword });
        toast.success(t`Password reset successfully`);
        void navigate({ to: '/login' });
      },
    })
  );

  return (
    <>
      <div className="mb-8 flex flex-col items-center gap-2">
        <div className="flex size-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
          <KeyRound size={24} />
        </div>
        <h1 className="mt-2 text-2xl font-bold tracking-tight text-gray-900">
          <Trans>Set new password</Trans>
        </h1>
        <p className="text-center text-sm text-gray-500">
          <Trans>Your new password must be at least 8 characters.</Trans>
        </p>
      </div>

      <form.AppForm>
        <form.FormElement>
          <FormErrors className="mb-4" />

          <div className="space-y-4">
            <form.AppField name="newPassword">
              {(field) => <field.TextField label={<Trans>New password</Trans>} type="password" />}
            </form.AppField>

            <form.AppField
              name="confirmPassword"
              validators={{
                onChange: ({ value, fieldApi }) => {
                  const newPassword = fieldApi.form.getFieldValue('newPassword');
                  if (value !== null && newPassword !== null && value !== newPassword) {
                    return t`Passwords do not match`;
                  }
                  return undefined;
                },
              }}
            >
              {(field) => (
                <field.TextField label={<Trans>Confirm password</Trans>} type="password" />
              )}
            </form.AppField>
          </div>

          <FormSubmit className="mt-6 w-full">
            <Trans>Reset password</Trans>
          </FormSubmit>
        </form.FormElement>
      </form.AppForm>

      <div className="mt-8 text-center text-sm text-gray-500">
        <Trans>Remember your password?</Trans>{' '}
        <RouterLink to="/login">
          <Trans>Sign in</Trans>
        </RouterLink>
      </div>
    </>
  );
};
